import React from 'react';
import './Policy.css';

/**
 * Disclaimer component displays the company's legal disclaimers and terms
 * Includes sections for general disclaimer, product-specific disclaimers,
 * technical limitations, intellectual property, liability, and contact information
 * @component
 * @returns {JSX.Element} The rendered Disclaimer component
 */
const Disclaimer = () => {
  return (
    <div className="policy-container">
      <h1>Disclaimer</h1>
      <p className="last-updated">Last Updated: May 30, 2025</p>

      <section>
        <h2>1. General Disclaimer</h2>
        <p>
          The information provided by TearFace ("we", "our", "us") on our website and through our products (Accessibility Widget, Chat Support Widget, and Digital Portfolio Services) is for general informational and service enhancement purposes only. All information and services are provided in good faith, but we make no representation or warranty of any kind, express or implied, regarding the accuracy, adequacy, validity, reliability, availability, or completeness of any information or services provided.
        </p>
      </section>

      <section>
        <h2>2. Product-Specific Disclaimers</h2>
        
        <h3>2.1 Accessibility Widget</h3>
        <p>By using our Accessibility Widget, you acknowledge that:</p>
        <ul>
          <li>The widget is designed to enhance website accessibility but does not guarantee full compliance with WCAG guidelines, ADA requirements, Section 508, EN 301 549, or other accessibility standards</li>
          <li>Effectiveness may vary based on your website's structure, content, and technical implementation</li>
          <li>User preferences and settings are stored locally and we are not responsible for any data loss</li>
          <li>Widget functionality may be affected by user browser settings, extensions, device configurations, or technical limitations</li>
          <li>Visual adjustments (contrast, colors, font sizes) may not apply to all elements due to technical constraints</li>
          <li>AI-powered features like page reading and text magnification are provided as-is without guarantee of accuracy</li>
          <li>Keyboard navigation enhancements may not function with all third-party scripts or dynamic content</li>
          <li>Animation controls and sound muting features may not affect all types of media or animations</li>
          <li>Dyslexic font and text spacing adjustments may not apply to embedded content or custom fonts</li>
          <li>Reading masks and reading line features may not work with scrolling animations or dynamic content loading</li>
        </ul>
        
        <p>Accessibility Compliance Notice:</p>
        <ul>
          <li>The widget is a supplementary tool and does not replace the need for accessible website design</li>
          <li>We do not guarantee that use of the widget will make your website compliant with any accessibility laws</li>
          <li>You remain solely responsible for your website's accessibility compliance</li>
          <li>Widget installations should be supplemented with regular accessibility audits</li>
          <li>We recommend consulting with accessibility experts and legal counsel for compliance needs</li>
        </ul>

        <p>Future Browser Extension Notice:</p>
        <ul>
          <li>The upcoming browser extension may have additional or different features from the widget</li>
          <li>Extension functionality may vary by browser type and version</li>
          <li>Extension settings may conflict with website-specific widget settings</li>
          <li>Data synchronization between extension and widget is not guaranteed</li>
          <li>Extension availability and functionality may vary by region or jurisdiction</li>
        </ul>

        <h3>2.2 Chat Support Widget</h3>
        <p>Regarding our AI-powered Chat Support Widget:</p>
        <ul>
          <li>AI-generated responses are provided "as is" without warranty of accuracy or appropriateness</li>
          <li>We are not responsible for any business decisions made based on AI responses</li>
          <li>The service may experience downtime for maintenance or technical updates</li>
          <li>Response quality depends on the training data and may not cover all specific business cases</li>
        </ul>

        <h3>2.3 Digital Portfolio Services</h3>
        <p>For our portfolio services:</p>
        <ul>
          <li>We do not guarantee specific traffic, engagement, or business outcomes from your portfolio</li>
          <li>Hosting services may experience occasional downtime for maintenance</li>
          <li>While we implement security measures, we cannot guarantee against all potential security threats</li>
          <li>Portfolio appearance may vary across different devices and browsers</li>
        </ul>
      </section>

      <section>
        <h2>3. Technical Limitations</h2>
        <p>Our services are subject to technical limitations including but not limited to:</p>
        <ul>
          <li>Browser compatibility constraints</li>
          <li>Internet connectivity requirements</li>
          <li>Server response times and availability</li>
          <li>Third-party service dependencies</li>
          <li>Device and operating system compatibility</li>
        </ul>
      </section>

      <section>
        <h2>4. Intellectual Property</h2>
        <p>
          While you retain ownership of your content and data, the products themselves, including all software, features, and functionalities, are owned by TearFace. Any unauthorized reproduction, modification, or distribution of our products is strictly prohibited.
        </p>
      </section>

      <section>
        <h2>5. Limitation of Liability</h2>
        <p>
          To the maximum extent permitted by law, TearFace shall not be liable for any:
        </p>
        <ul>
          <li>Direct, indirect, incidental, consequential, or punitive damages</li>
          <li>Loss of profits, revenue, data, or business opportunities</li>
          <li>Issues arising from unauthorized access or cyber attacks</li>
          <li>Damages resulting from service interruptions or data loss</li>
          <li>Claims related to accessibility compliance or lack thereof</li>
        </ul>
      </section>

      <section>
        <h2>6. Changes and Updates</h2>
        <p>
          WE RESERVE THE ABSOLUTE AND UNCONDITIONAL RIGHT, AT OUR SOLE DISCRETION AND WITHOUT ANY PRIOR NOTICE OR LIABILITY, TO:
        </p>
        <ul>
          <li>Modify, suspend, or discontinue any or all services, features, or functionalities</li>
          <li>Update or change pricing, payment terms, and service conditions</li>
          <li>Modify any part of this disclaimer, Terms of Service, Privacy Policy, or any other policies</li>
          <li>Deploy system updates, security patches, or other modifications</li>
          <li>Limit or restrict access to any or all services</li>
          <li>Remove or modify any content or data within our services</li>
        </ul>
        <p>
          YOU EXPRESSLY AGREE THAT:
        </p>
        <ul>
          <li>We have no obligation to provide notice of any changes</li>
          <li>Your continued use of any services after any changes constitutes your irrevocable acceptance of such changes</li>
          <li>You are solely responsible for reviewing our policies regularly</li>
          <li>No refunds or compensation will be provided for any changes or discontinuation of services</li>
        </ul>
        <p>
          THIS SECTION SUPERSEDES ANY CONFLICTING TERMS IN ANY OTHER AGREEMENTS BETWEEN YOU AND TEARFACE.
        </p>
      </section>

      <section>
        <h2>7. Indemnification</h2>
        <p>
          You agree to indemnify, defend, and hold harmless TearFace, its officers, directors, employees, agents, and third parties from and against all losses, expenses, damages, and costs, including reasonable attorneys' fees, resulting from:
        </p>
        <ul>
          <li>Any violation of these terms by you</li>
          <li>Any use or misuse of our products</li>
          <li>Any claims related to your content or usage</li>
          <li>Any breach of applicable laws or regulations</li>
        </ul>
      </section>

      <section>
        <h2>8. Governing Law and Jurisdiction</h2>
        <p>
          THESE TERMS AND YOUR USE OF OUR SERVICES SHALL BE GOVERNED BY AND CONSTRUED IN ACCORDANCE WITH THE LAWS OF THE JURISDICTION WHERE TEARFACE IS LOCATED, WITHOUT REGARD TO ITS CONFLICT OF LAW PROVISIONS. YOU EXPRESSLY AGREE THAT:
        </p>
        <ul>
          <li>Exclusive Jurisdiction:
            <ul>
              <li>Any legal action or proceeding shall be brought exclusively in the courts of the jurisdiction where TearFace is located</li>
              <li>You consent to the jurisdiction of such courts and waive any objection to venue</li>
              <li>You waive any right to a trial by jury</li>
              <li>You agree that service of process may be made by certified mail, return receipt requested</li>
              <li>We reserve the right to change our jurisdiction of operation at any time</li>
            </ul>
          </li>
          <li>Class Action Waiver:
            <ul>
              <li>You waive any right to participate in class actions</li>
              <li>You waive any right to consolidate claims with others</li>
              <li>You waive any right to bring claims as a representative or member of a class</li>
              <li>You waive any right to bring claims as a private attorney general</li>
              <li>You agree that any claims must be brought individually</li>
            </ul>
          </li>
          <li>Time Limitation:
            <ul>
              <li>Any claim must be brought within six (6) months of the date the claim arose</li>
              <li>You waive any statute of limitations to the contrary</li>
              <li>This time limitation applies to all claims, whether based on contract, tort, or other legal theory</li>
              <li>No claims can be brought after this period under any circumstances</li>
            </ul>
          </li>
          <li>Severability:
            <ul>
              <li>If any part of this section is found unenforceable, the remaining parts remain in effect</li>
              <li>If a court finds any part unenforceable, it shall be modified to the minimum extent necessary</li>
              <li>No court shall have the power to modify these terms</li>
            </ul>
          </li>
        </ul>
      </section>

      <section>
        <h2>9. "AS IS" Warranty Disclaimer</h2>
        <p>
          ALL SERVICES ARE PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTY OF ANY KIND, EITHER EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO THE IMPLIED WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, OR NON-INFRINGEMENT. WE MAKE NO WARRANTY THAT:
        </p>
        <ul>
          <li>THE SERVICES WILL MEET YOUR REQUIREMENTS</li>
          <li>THE SERVICES WILL BE UNINTERRUPTED, TIMELY, SECURE, OR ERROR-FREE</li>
          <li>THE RESULTS OBTAINED FROM USE OF THE SERVICES WILL BE ACCURATE OR RELIABLE</li>
          <li>ANY ERRORS IN THE SERVICES WILL BE CORRECTED</li>
        </ul>
      </section>

      <section>
        <h2>10. Export Control & Sanctions</h2>
        <p>
          Our products may be subject to U.S. export control laws and may be subject to export or import regulations in other countries. You agree to strictly comply with all such laws and regulations and acknowledge that you have the responsibility to obtain authorization to export, re-export, or import our products.
        </p>
      </section>

      <section>
        <h2>11. No Legal Advice</h2>
        <p>
          Nothing contained in our services, including the Accessibility Widget, constitutes legal advice. Any information or suggestions provided are for general informational purposes only and should not be relied upon for compliance with any laws, regulations, or standards.
        </p>
      </section>

      <section>
        <h2>12. Force Majeure</h2>
        <p>
          TearFace shall not be liable for any failure or delay in performance due to circumstances beyond our reasonable control, including but not limited to:
        </p>
        <ul>
          <li>Acts of God, war, terrorism, or civil unrest</li>
          <li>Fire, explosion, or accident</li>
          <li>Internet or telecommunications failures</li>
          <li>Government actions or restrictions</li>
          <li>Labor strikes or difficulties</li>
        </ul>
      </section>

      <section>
        <h2>13. Contact Information</h2>
        <p>
          For questions about this disclaimer or our services, please contact:
        </p>
        <p>Email: hellotearface@gmail.com</p>
      </section>

      <section>
        <h2>14. Class Action Waiver and Mandatory Arbitration</h2>
        <p>
          YOU AGREE TO WAIVE ANY RIGHT TO PARTICIPATE IN CLASS ACTION LAWSUITS OR CLASS-WIDE ARBITRATION OF ANY CLAIMS. ALL DISPUTES, WHETHER IN COURT OR ARBITRATION, SHALL BE BROUGHT SOLELY IN YOUR INDIVIDUAL CAPACITY.
        </p>
        <p>
          ANY AND ALL CLAIMS, DISPUTES, OR CONTROVERSIES ARISING OUT OF OR RELATING TO THE USE OF OUR SERVICES SHALL BE SUBMITTED TO FINAL AND BINDING ARBITRATION:
        </p>
        <ul>
          <li>Arbitration Rules:
            <ul>
              <li>Arbitration shall be conducted by an arbitration body of our choosing</li>
              <li>We reserve the right to select the arbitration body and rules</li>
              <li>The arbitrator's decision shall be final and binding</li>
              <li>Judgment on the award may be entered in any court having jurisdiction</li>
              <li>You waive any right to challenge the selection of the arbitration body</li>
            </ul>
          </li>
          <li>Arbitration Process:
            <ul>
              <li>Arbitration shall be conducted in English</li>
              <li>Each party shall bear its own costs</li>
              <li>The arbitrator may award reasonable attorneys' fees to the prevailing party</li>
              <li>We reserve the right to change the location of arbitration</li>
              <li>All proceedings shall be confidential</li>
            </ul>
          </li>
          <li>Exceptions:
            <ul>
              <li>Small claims court cases are exempt from mandatory arbitration</li>
              <li>Intellectual property disputes may be brought in court</li>
              <li>Injunctive relief may be sought in court</li>
              <li>We reserve the right to bring any claims in court</li>
            </ul>
          </li>
          <li>Pre-Arbitration Requirements:
            <ul>
              <li>Before pursuing any legal action, you must:
                <ul>
                  <li>Contact us first with any concerns</li>
                  <li>Allow us 30 days to address your concerns</li>
                  <li>Participate in good-faith negotiations</li>
                  <li>Attempt mediation through a mutually agreed third party</li>
                </ul>
              </li>
              <li>No legal action may be taken until these steps are completed</li>
              <li>We reserve the right to choose the mediator</li>
              <li>All discussions must be in English</li>
            </ul>
          </li>
        </ul>
      </section>

      <section>
        <h2>15. Copyright and Intellectual Property</h2>
        <p>
          ALL CONTENT, FEATURES, AND FUNCTIONALITY OF OUR SERVICES, INCLUDING BUT NOT LIMITED TO ALL INFORMATION, SOFTWARE, TEXT, DISPLAYS, IMAGES, VIDEO, AUDIO, DESIGN, SELECTION, AND ARRANGEMENT THEREOF, ARE OWNED BY TEARFACE, ITS LICENSORS, OR OTHER PROVIDERS AND ARE PROTECTED BY COPYRIGHT, TRADEMARK, PATENT, TRADE SECRET, AND OTHER INTELLECTUAL PROPERTY OR PROPRIETARY RIGHTS LAWS.
        </p>
        <p>
          ANY UNAUTHORIZED USE, REPRODUCTION, MODIFICATION, DISTRIBUTION, TRANSMISSION, REPUBLICATION, DISPLAY, OR PERFORMANCE OF THE CONTENT ON THIS SITE IS STRICTLY PROHIBITED AND MAY RESULT IN SERIOUS CIVIL AND CRIMINAL PENALTIES.
        </p>
      </section>

      <section>
        <h2>16. Severability and Survival</h2>
        <p>
          If any provision of this disclaimer is found to be unenforceable or invalid under any applicable law, such unenforceability or invalidity shall not render this disclaimer unenforceable or invalid as a whole. Such provisions shall be deleted without affecting the remaining provisions herein, which shall remain in full force and effect.
        </p>
        <p>
          The limitations of liability, indemnification, arbitration provisions, and all other provisions that by their nature should survive, shall survive the termination of your use of our services.
        </p>
      </section>

      <section>
        <h2>17. No Waiver and Severability</h2>
        <p>
          If any part of this Disclaimer is found to be invalid, unenforceable, or incomplete, the remaining provisions will remain in full force and effect. Our failure to enforce any right or provision of this disclaimer does not constitute a waiver of that right or provision. No omission, error, or imperfection in this document will create any liability or waive any limitation of liability, and you agree that our liability remains limited as described herein regardless of any such issue.
        </p>
      </section>

      <section>
        <h2>18. Complete Agreement and Waiver</h2>
        <p>
          THIS DISCLAIMER, TOGETHER WITH OUR TERMS OF SERVICE AND PRIVACY POLICY, CONSTITUTES THE ENTIRE AGREEMENT BETWEEN YOU AND TEARFACE REGARDING YOUR USE OF OUR SERVICES. NO WAIVER OF ANY TERM SHALL BE DEEMED A FURTHER OR CONTINUING WAIVER OF SUCH TERM OR ANY OTHER TERM.
        </p>
        <p>
          BY USING OUR SERVICES, YOU EXPRESSLY AND IRREVOCABLY WAIVE ANY AND ALL CLAIMS, RIGHTS, OR REMEDIES YOU MAY HAVE AGAINST TEARFACE, WHETHER ARISING FROM CONTRACT, TORT, STATUTE, OR OTHERWISE, TO THE FULLEST EXTENT PERMITTED BY LAW.
        </p>
      </section>

      <section>
        <h2>19. Final Acknowledgment and Absolute Waiver</h2>
        <p>
          BY USING OUR SERVICES, YOU ACKNOWLEDGE AND IRREVOCABLY AGREE THAT:
        </p>
        <ul>
          <li>YOU HAVE READ, UNDERSTOOD, AND AGREED TO ALL TERMS IN THEIR ENTIRETY</li>
          <li>YOU PERMANENTLY AND UNCONDITIONALLY WAIVE ALL RIGHTS TO SUE OR MAKE CLAIMS AGAINST TEARFACE</li>
          <li>YOU WILL NEVER ATTEMPT TO JOIN OR INITIATE ANY CLASS ACTION AGAINST TEARFACE</li>
          <li>YOU ACCEPT ALL RISKS AND RESPONSIBILITIES, INCLUDING TOTAL LOSS OF DATA OR BUSINESS</li>
          <li>EVEN IN CASE OF OUR NEGLIGENCE OR SYSTEM FAILURE, YOU CANNOT TAKE LEGAL ACTION</li>
          <li>THIS WAIVER APPLIES TO ALL POSSIBLE CLAIMS, KNOWN OR UNKNOWN, FOREVER</li>
        </ul>
        <p>
          THIS ACKNOWLEDGMENT IS ABSOLUTE, IRREVOCABLE, AND SUPERSEDES ALL PRIOR AGREEMENTS OR UNDERSTANDINGS. IF YOU DO NOT AGREE TO ANY PART OF THIS, YOU MUST CEASE USING ALL TEARFACE SERVICES IMMEDIATELY.
        </p>
      </section>

      <section>
        <h2>20. Marketing Statistics and Claims</h2>
        <p>
          ALL STATISTICS, METRICS, AND MARKETING CLAIMS PROVIDED ON THIS WEBSITE ARE:
        </p>
        <ul>
          <li>Not guaranteed results</li>
          <li>Based on various third-party sources and general market research</li>
          <li>Subject to change without notice</li>
          <li>May not apply to your specific situation</li>
          <li>Should not be considered as financial, legal, or business advice</li>
        </ul>
        <p>
          By using our services, you acknowledge that any performance metrics, statistics, or marketing claims (including but not limited to conversion rates, engagement rates, accessibility statistics, and portfolio performance numbers) are provided for informational purposes only. TearFace makes no warranties about the accuracy, completeness, or reliability of such information.
        </p>
      </section>

      <section>
        <h2>21. Additional Protections and Waivers</h2>
        <p>
          YOU EXPRESSLY ACKNOWLEDGE AND IRREVOCABLY AGREE THAT:
        </p>
        <ul>
          <li>Complete Waiver of Rights:
            <ul>
              <li>You waive all rights to sue, claim, or demand compensation from TearFace</li>
              <li>You waive all rights to join or initiate any class action or mass action</li>
              <li>You waive all rights to bring claims in any forum other than specified herein</li>
              <li>You waive all rights to challenge the validity or enforceability of these terms</li>
              <li>You waive all rights to seek injunctive or equitable relief</li>
              <li>You waive all rights to appeal any decision</li>
            </ul>
          </li>
          <li>No Guarantees:
            <ul>
              <li>We make no guarantees about service availability, performance, or results</li>
              <li>We make no guarantees about accessibility compliance or improvements</li>
              <li>We make no guarantees about data security or privacy</li>
              <li>We make no guarantees about third-party integrations</li>
              <li>All services are provided "as is" without any warranties</li>
            </ul>
          </li>
          <li>Indemnification:
            <ul>
              <li>You agree to indemnify us against all claims, damages, and expenses</li>
              <li>You agree to pay our legal fees if you bring any claims</li>
              <li>You agree to pay our costs of defending any claims</li>
              <li>This indemnification survives termination of service</li>
            </ul>
          </li>
          <li>Force Majeure:
            <ul>
              <li>We are not liable for any failure due to circumstances beyond our control</li>
              <li>This includes but is not limited to:
                <ul>
                  <li>Natural disasters</li>
                  <li>War, terrorism, or civil unrest</li>
                  <li>Government actions or regulations</li>
                  <li>Technical failures or cyber attacks</li>
                  <li>Third-party service disruptions</li>
                </ul>
              </li>
            </ul>
          </li>
          <li>Service Modifications:
            <ul>
              <li>We may modify or discontinue any service at any time</li>
              <li>We may change pricing, features, or terms without notice</li>
              <li>No refunds or compensation for service changes</li>
              <li>Your continued use constitutes acceptance of changes</li>
            </ul>
          </li>
          <li>Data and Privacy:
            <ul>
              <li>We make no guarantees about data security</li>
              <li>We may modify our data practices without notice</li>
              <li>You are responsible for backing up your data</li>
              <li>We may delete data at any time</li>
            </ul>
          </li>
          <li>Third-Party Services:
            <ul>
              <li>We are not responsible for third-party services</li>
              <li>We make no guarantees about third-party integrations</li>
              <li>You use third-party services at your own risk</li>
              <li>We may remove third-party integrations at any time</li>
            </ul>
          </li>
          <li>Termination:
            <ul>
              <li>We may terminate service at any time without cause</li>
              <li>No refunds upon termination</li>
              <li>We may delete all data upon termination</li>
              <li>You must cease all use upon termination</li>
            </ul>
          </li>
        </ul>
      </section>

      <section>
        <h2>22. Final Waiver and Release</h2>
        <p>
          BY USING OUR SERVICES, YOU HEREBY IRREVOCABLY AND UNCONDITIONALLY:
        </p>
        <ul>
          <li>Release and forever discharge TearFace from any and all claims, demands, and causes of action</li>
          <li>Waive any and all rights to sue, claim, or demand compensation</li>
          <li>Agree that these terms are a complete defense to any claim</li>
          <li>Acknowledge that you have read and understood all terms</li>
          <li>Agree that these terms are fair and reasonable</li>
          <li>Accept all risks associated with using our services</li>
          <li>Agree that no oral or written representations have been made</li>
          <li>Confirm that you are of legal age and capacity to agree to these terms</li>
        </ul>
        <p>
          IF YOU DO NOT AGREE TO ANY PART OF THESE TERMS, YOU MUST CEASE USING ALL TEARFACE SERVICES IMMEDIATELY.
        </p>
      </section>
    </div>
  );
};

export default Disclaimer;
